/**
 * UploadQueuePanel — in-app dashboard for the resumable upload queue.
 *
 * Lists every incomplete batch and its rows with live status. Per-row
 * affordances by state:
 *   - pending             → spinner, will be picked up when worker pool runs
 *   - pending-needs-file  → "Browse" button; opens single-file picker
 *                           and registers the file on the queue
 *   - extracting / summarizing / uploading → live spinner
 *   - failed              → "Retry" button → flips back to pending
 *   - failed-permanent    → read-only, with reason. If reason is
 *                           file-not-found-on-resume, "Browse" appears
 *                           as a recovery affordance
 *   - completed           → green check
 *   - skipped-duplicate   → muted "duplicate" badge
 *
 * Per-batch: Pause / Resume / Cancel + Start Processing. Live polling
 * every 1.5s while open. Closed = no polling, no resource cost.
 *
 * Mounted in App.tsx. Opened via the `open-upload-queue-panel` custom
 * event (matches the AdminPanel + UserApiKeysSettings event-bus pattern).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { X, Loader2, RefreshCw, Pause, Play, Trash2, Folder, CheckCircle2, AlertTriangle, FileQuestion, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import type { UploadBatch, UploadQueueRow, UploadStatus, UploadQueue } from '../services/upload/uploadQueue';
import { createUploadProcessor } from '../services/upload/uploadProcessor';

const POLL_MS = 1500;

interface Props {
  open: boolean;
  onClose: () => void;
}

/* ── Status badges ─────────────────────────────────────────────────── */

const STATUS_LABEL: Record<UploadStatus, string> = {
  'pending':              'Pending',
  'pending-needs-file':   'Needs file',
  'extracting':           'Extracting…',
  'summarizing':          'Summarising…',
  'indexing':             'Indexing…',
  'uploading':            'Uploading…',
  'completed':            'Completed',
  'failed':               'Failed (retrying)',
  'failed-permanent':     'Failed',
  'cancelled':            'Cancelled',
  'skipped-duplicate':    'Duplicate',
};

const STATUS_TONE: Record<UploadStatus, string> = {
  'pending':              'bg-state-hover text-text-secondary',
  'pending-needs-file':   'bg-warning/10 text-warning border-warning/40',
  'extracting':           'bg-primary/10 text-primary',
  'summarizing':          'bg-primary/10 text-primary',
  'indexing':             'bg-primary/10 text-primary',
  'uploading':            'bg-primary/10 text-primary',
  'completed':            'bg-success/10 text-success border-success/40',
  'failed':               'bg-warning/10 text-warning border-warning/40',
  'failed-permanent':     'bg-danger/10 text-danger border-danger/40',
  'cancelled':            'bg-state-hover text-text-muted',
  'skipped-duplicate':    'bg-state-hover text-text-muted',
};

function StatusBadge({ status }: { status: UploadStatus }) {
  const isInFlight = status === 'extracting' || status === 'summarizing' || status === 'indexing' || status === 'uploading';
  return (
    <span className={`inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 border ${STATUS_TONE[status]}`}>
      {isInFlight && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
      {status === 'completed' && <CheckCircle2 className="w-2.5 h-2.5" />}
      {(status === 'failed' || status === 'failed-permanent') && <AlertTriangle className="w-2.5 h-2.5" />}
      {status === 'pending-needs-file' && <FileQuestion className="w-2.5 h-2.5" />}
      {STATUS_LABEL[status]}
    </span>
  );
}

/* ── Row + batch actions ───────────────────────────────────────────── */

function pickFile(): Promise<File | null> {
  return new Promise<File | null>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.oncancel = () => resolve(null);
    input.click();
  });
}

interface RowActionsProps {
  row: UploadQueueRow;
  queue: UploadQueue;
  onChange: () => void;
}

function RowActions({ row, queue, onChange }: RowActionsProps) {
  const [busy, setBusy] = useState(false);

  const browseForFile = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const file = await pickFile();
      if (!file) return;
      if (file.size !== row.sizeBytes) {
        toast.warning(`File size differs (${file.size} vs ${row.sizeBytes}). Skipping rebind.`);
        return;
      }
      queue.registerFile(row.id, file);
      await queue.updateRow(row.id, { status: 'pending', lastError: undefined });
      toast.success(`${row.fileName} re-bound — will process on next run.`);
      onChange();
    } finally {
      setBusy(false);
    }
  }, [busy, row, queue, onChange]);

  const retry = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      // 'failed' rows are already auto-requeued by the worker pool's
      // runOne — manual retry just flips the status back to pending in
      // case the user wants to nudge a stuck queue.
      await queue.updateRow(row.id, { status: 'pending', lastError: undefined });
      onChange();
    } finally { setBusy(false); }
  }, [busy, row.id, queue, onChange]);

  if (row.status === 'pending-needs-file') {
    return (
      <button
        onClick={browseForFile}
        disabled={busy}
        className="text-[8px] font-bold uppercase tracking-widest text-warning hover:text-warning/80 px-2 py-0.5 rounded-md border border-warning/40 hover:bg-warning/10 disabled:opacity-50 inline-flex items-center gap-1"
        data-testid={`row-${row.id}-browse`}
      >
        <FolderOpen className="w-2.5 h-2.5" /> Browse
      </button>
    );
  }
  if (row.status === 'failed-permanent' && row.lastError === 'file-not-found-on-resume') {
    return (
      <button
        onClick={browseForFile}
        disabled={busy}
        className="text-[8px] font-bold uppercase tracking-widest text-text-secondary hover:text-text-primary px-2 py-0.5 border border-border hover:bg-state-hover disabled:opacity-50 inline-flex items-center gap-1"
        data-testid={`row-${row.id}-rebind`}
      >
        <FolderOpen className="w-2.5 h-2.5" /> Re-bind
      </button>
    );
  }
  if (row.status === 'failed') {
    return (
      <button
        onClick={retry}
        disabled={busy}
        className="text-[8px] font-bold uppercase tracking-widest text-warning hover:text-warning/80 px-2 py-0.5 rounded-md border border-warning/40 hover:bg-warning/10 disabled:opacity-50 inline-flex items-center gap-1"
        data-testid={`row-${row.id}-retry`}
      >
        <RefreshCw className="w-2.5 h-2.5" /> Retry
      </button>
    );
  }
  return null;
}

/* ── Panel ─────────────────────────────────────────────────────────── */

interface BatchSection {
  batch: UploadBatch;
  rows: UploadQueueRow[];
}

export const UploadQueuePanel: React.FC<Props> = ({ open, onClose }) => {
  const [queue, setQueue] = useState<UploadQueue | null>(null);
  const [sections, setSections] = useState<BatchSection[]>([]);
  const [processingBatchId, setProcessingBatchId] = useState<string | null>(null);

  // Lazy-load the queue on first open (the singleton creates an IDB
  // connection; cheap to re-read while the panel stays mounted).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const { getUploadQueue } = await import('../services/upload/uploadQueue');
      if (!cancelled) setQueue(getUploadQueue());
    })();
    return () => { cancelled = true; };
  }, [open]);

  const refresh = useCallback(async () => {
    if (!queue) return;
    const batches = await queue.listIncompleteBatches();
    const next: BatchSection[] = [];
    for (const b of batches) {
      const rows = await queue.listRows(b.id);
      next.push({ batch: b, rows });
    }
    setSections(next);
  }, [queue]);

  // Live polling while the panel is open.
  useEffect(() => {
    if (!open || !queue) return;
    void refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [open, queue, refresh]);

  const handlePause = useCallback(async (batchId: string) => {
    if (!queue) return;
    await queue.pauseBatch(batchId);
    void refresh();
  }, [queue, refresh]);

  const handleResume = useCallback(async (batchId: string) => {
    if (!queue) return;
    await queue.resumeBatch(batchId);
    void refresh();
  }, [queue, refresh]);

  const handleCancel = useCallback(async (batchId: string) => {
    if (!queue) return;
    // PH-C2 — replace window.confirm with the canonical openConfirmDialog
    // primitive (portal-backed AlertDialog) for a11y + iframe-safety.
    // Semantics preserved: prompt copy + gating identical.
    const { openConfirmDialog } = await import('./ui/confirmDialog');
    const ok = await openConfirmDialog({
      title: 'Cancel batch?',
      body: 'Cancel this batch? Pending uploads will not run.',
      confirmLabel: 'Cancel batch',
      cancelLabel: 'Keep batch',
      tone: 'danger',
    });
    if (!ok) return;
    await queue.cancelBatch(batchId);
    toast.success('Batch cancelled.');
    void refresh();
  }, [queue, refresh]);

  const handleStart = useCallback(async (section: BatchSection) => {
    if (!queue || processingBatchId) return;
    setProcessingBatchId(section.batch.id);
    try {
      // Resume might still be in-flight if the user just clicked Resume —
      // resumeBatch from the banner is idempotent so we don't double-call
      // here. The worker pool will skip rows whose handle is missing
      // (auto-flips them to pending-needs-file).
      const processRow = createUploadProcessor({
        siteId: section.batch.siteId,
        reportId: section.batch.reportId,
      });
      await queue.processBatch(section.batch.id, { processRow, concurrency: 4 });
      toast.success(`Batch "${section.batch.label ?? section.batch.id}" finished.`);
    } catch (e) {
      toast.error(`Processing failed: ${(e as Error)?.message ?? 'unknown error'}`);
    } finally {
      setProcessingBatchId(null);
      void refresh();
    }
  }, [queue, processingBatchId, refresh]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
      onClick={onClose}
      data-testid="upload-queue-panel"
    >
      <div
        className="bg-background w-[calc(100vw-2rem)] max-w-[720px] max-h-[85vh] overflow-y-auto shadow-2xl border border-border rounded-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start gap-3">
          <div className="w-9 h-9 border border-border bg-state-hover flex items-center justify-center shrink-0">
            <Folder className="w-4 h-4 text-text-secondary" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold uppercase tracking-widest text-text-primary">Upload queue</h2>
            <p className="text-[10px] text-text-secondary mt-0.5">
              {sections.length === 0
                ? 'No incomplete batches.'
                : `${sections.length} batch${sections.length === 1 ? '' : 'es'} — ${sections.reduce((acc, s) => acc + s.rows.length, 0)} rows`}
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-text-muted hover:text-text-primary" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {sections.length === 0 ? (
            <div className="text-[11px] uppercase tracking-widest text-text-muted text-center py-8" data-testid="queue-empty">
              Nothing to do — uploads will appear here while they're in flight.
            </div>
          ) : sections.map(section => {
            const { batch, rows } = section;
            const counts: Partial<Record<UploadStatus, number>> = {};
            for (const r of rows) counts[r.status] = (counts[r.status] ?? 0) + 1;
            const isProcessing = processingBatchId === batch.id;

            return (
              <div
                key={batch.id}
                className="border border-border bg-state-hover"
                data-testid={`batch-${batch.id}`}
              >
                {/* Batch header */}
                <div className="px-4 py-3 border-b border-border flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-bold uppercase tracking-widest text-text-primary truncate">
                      {batch.label ?? batch.id}
                    </div>
                    <div className="text-[10px] text-text-muted mt-0.5 font-mono">
                      site: {batch.siteId} · {batch.totalFiles} files · created {new Date(batch.createdAt).toLocaleString()}
                      {batch.paused && ' · paused'}
                      {batch.cancelled && ' · cancelled'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {batch.paused
                      ? <button onClick={() => handleResume(batch.id)} className="text-[8px] font-bold uppercase tracking-widest text-text-secondary hover:text-text-primary px-2 py-1 border border-border hover:bg-state-active inline-flex items-center gap-1">
                          <Play className="w-2.5 h-2.5" /> Resume
                        </button>
                      : <button onClick={() => handlePause(batch.id)} className="text-[8px] font-bold uppercase tracking-widest text-text-secondary hover:text-text-primary px-2 py-1 border border-border hover:bg-state-active inline-flex items-center gap-1">
                          <Pause className="w-2.5 h-2.5" /> Pause
                        </button>}
                    <button
                      onClick={() => handleStart(section)}
                      disabled={isProcessing || batch.paused || batch.cancelled}
                      className="text-[8px] font-bold uppercase tracking-widest text-white bg-primary hover:bg-primary/90 px-2 py-1 border border-primary disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                      data-testid={`batch-${batch.id}-start`}
                    >
                      {isProcessing && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
                      {isProcessing ? 'Processing…' : 'Start'}
                    </button>
                    <button
                      onClick={() => handleCancel(batch.id)}
                      className="text-[8px] font-bold uppercase tracking-widest text-text-secondary hover:text-danger px-2 py-1 border border-border hover:border-danger/40 inline-flex items-center gap-1"
                      data-testid={`batch-${batch.id}-cancel`}
                    >
                      <Trash2 className="w-2.5 h-2.5" /> Cancel
                    </button>
                  </div>
                </div>

                {/* Status mini-summary */}
                <div className="px-4 py-2 border-b border-border-subtle flex flex-wrap gap-1.5">
                  {(Object.entries(counts) as Array<[UploadStatus, number]>).map(([s, n]) => (
                    <span key={s} className="inline-flex items-center gap-1">
                      <StatusBadge status={s} />
                      <span className="text-[8px] text-text-muted">×{n}</span>
                    </span>
                  ))}
                </div>

                {/* Row list */}
                <div className="max-h-[260px] overflow-y-auto">
                  {rows.map(row => (
                    <div
                      key={row.id}
                      className="px-4 py-2 border-b border-border-subtle last:border-b-0 flex items-center gap-3"
                      data-testid={`row-${row.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold text-text-primary truncate">{row.fileName}</div>
                        <div className="text-[9px] text-text-muted truncate font-mono">
                          {row.relativePath} · {(row.sizeBytes / 1024).toFixed(1)} KB
                          {row.attempts > 0 && ` · ${row.attempts} attempt${row.attempts === 1 ? '' : 's'}`}
                        </div>
                        {row.lastError && (
                          <div className="text-[9px] text-danger mt-0.5 truncate" title={row.lastError}>
                            {row.lastError}
                          </div>
                        )}
                      </div>
                      <StatusBadge status={row.status} />
                      {queue && <RowActions row={row} queue={queue} onChange={refresh} />}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
